import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { authService } from '@/services/auth.service';
import { router } from '@/router';

import type { AuthUser, LoginRequest } from '@/types/auth.types';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const user = ref<AuthUser | null>(null);
    const accessToken = ref<string | null>(null);
    const refreshToken = ref<string | null>(null);

    const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null);
    const userRole = computed(() => user.value?.role ?? null);
    const isAdmin = computed(() => userRole.value === 'admin');
    const isSupervisor = computed(() => userRole.value === 'supervisor');
    const isTechnicalDepartment = computed(() => userRole.value === 'technical_department');

    async function login(credentials: LoginRequest): Promise<void> {
      const response = await authService.login(credentials);

      accessToken.value = response.tokens.accessToken;
      refreshToken.value = response.tokens.refreshToken;
      user.value = response.user;
    }

    async function refresh(): Promise<void> {
      if (!refreshToken.value) {
        clearAuth();
        throw new Error('No refresh token');
      }

      try {
        const response = await authService.refresh(refreshToken.value);

        accessToken.value = response.tokens.accessToken;
        refreshToken.value = response.tokens.refreshToken;
        user.value = response.user;
      } catch {
        clearAuth();
        throw new Error('Token refresh failed');
      }
    }

    async function logout(): Promise<void> {
      try {
        await authService.logout();
      } catch {
        // Ignore errors during logout — clear local state regardless
      }

      clearAuth();
      await router.push('/login');
    }

    function clearAuth(): void {
      user.value = null;
      accessToken.value = null;
      refreshToken.value = null;
    }

    return {
      user,
      accessToken,
      refreshToken,
      isAuthenticated,
      userRole,
      isAdmin,
      isSupervisor,
      isTechnicalDepartment,
      login,
      refresh,
      logout,
    };
  },
  {
    persist: {
      paths: ['user', 'accessToken', 'refreshToken'],
    },
  },
);
